import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useBlogs, useBlogCategories } from "@/lib/queries";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

function Card({ b, featured }) {
  return (
    <Link
      to={`/blog/${b.slug}`}
      data-testid={`blog-item-${b.slug}`}
      className={`group flex overflow-hidden border border-white/10 bg-neutral-900 transition-colors hover:border-gold/40 ${
        featured ? "flex-col md:col-span-2 lg:col-span-3 md:flex-row" : "h-full flex-col"
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "md:w-1/2 aspect-[16/10] md:aspect-auto" : "aspect-[16/10]"}`}>
        <img src={`${b.cover_image}?w=640`} alt={b.title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale-[0.2] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
        {featured && <span className="absolute left-4 top-4 border border-gold/40 bg-black/70 px-3 py-1 font-oswald text-[11px] font-bold uppercase tracking-[0.15em] text-gold backdrop-blur">Öne Çıkan</span>}
      </div>
      <div className={`flex flex-1 flex-col p-5 ${featured ? "md:justify-center md:p-9" : ""}`}>
        <h2 className={`font-display font-medium leading-snug text-white transition-colors group-hover:text-gold ${featured ? "text-2xl sm:text-3xl" : "text-lg"}`}>{b.title}</h2>
        <p className={`mt-2 flex-1 text-neutral-400 ${featured ? "text-base line-clamp-3" : "text-sm line-clamp-3"}`}>{b.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 font-oswald text-xs uppercase tracking-[0.12em] text-gold">Devamını Oku <ArrowUpRight className="h-3.5 w-3.5" /></span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [cat, setCat] = useState("");
  const { data } = useBlogs(cat);
  const { data: categories = [] } = useBlogCategories();
  const items = data?.items || [];
  const showFeatured = !cat && items.length > 0;

  return (
    <>
      <Seo
        title="Araç Bakım Rehberi (Blog) | Meguiar's İzmit"
        description="Seramik kaplama, PPF, boya koruma, iç temizlik ve cam filmi hakkında uzman rehberler. Kocaeli Araç Bakım Rehberi."
        canonicalPath="/blog"
        jsonLd={breadcrumbSchema([{ label: "Ana Sayfa", to: "/" }, { label: "Blog" }])}
      />
      <PageHero
        overline="Araç Bakım Rehberi"
        title="Kocaeli Araç Bakım Rehberi"
        subtitle="Aracınızı korumak, parlatmak ve değerini sürdürmek için bilmeniz gereken her şey; uzman ağzından."
        breadcrumbs={[{ label: "Ana Sayfa", to: "/" }, { label: "Blog" }]}
      />

      <section className="obsidian py-14 sm:py-20">
        <div className="container-x">
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => setCat("")} data-testid="blog-cat-all" className={`border px-4 py-2 font-oswald text-xs uppercase tracking-[0.08em] transition-colors ${!cat ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-white"}`}>Tümü</button>
            {categories.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`blog-cat-${c.slug}`} className={`border px-4 py-2 font-oswald text-xs uppercase tracking-[0.08em] transition-colors ${cat === c.slug ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-white"}`}>{c.name}</button>
            ))}
          </div>

          {items.length === 0 ? (
            <p className="mt-12 text-neutral-500">Bu kategoride henüz yazı bulunmuyor.</p>
          ) : (
            <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {items.map((b, i) => (
                <Reveal key={b.slug} delay={(i % 3) * 0.06}>
                  <Card b={b} featured={showFeatured && i === 0} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
